
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'

// #681: a ROLLING 4-week window (the current week + the next three), not a
// fixed calendar month. Late in a month the old grid showed four spent weeks
// with nothing bookable and made the client page forward to find the real
// openings; the rolling window always leads with today. The month header
// becomes a range label when the window straddles months, and availability
// is still fetched via the month-granular /dates/ endpoint — one request per
// month the window touches, cached, so a straddling window costs at most one
// extra call.
const WINDOW_WEEKS = 4
const WINDOW_DAYS = WINDOW_WEEKS * 7
// The last day an AEP change can be made — /book/ refuses later dates too.
const AEP_LAST_MONTH = 11 // December
const AEP_LAST_DAY = 7

export default {
    props: {
        // {date: 'YYYY-MM-DD'|null, time: ISOstring|null, agent: number|null, agents: array|null}
        value: { type: Object, required: true },
        // List of agents we should query for availability
        agents: { type: Array, default: () => [] },
        // 'phone' | 'video'
        method: { type: String, default: 'phone' },
        // Pre-fetched soonest day per method ({ phone: 'YYYY-MM-DD'|null, video: ... }).
        // Used until our own /dates/ response lands so the pill is instant.
        soonest: { type: Object, default: () => ({}) },
        aep: { type: Boolean, default: false },
        timezone: { type: String, default: 'America/New_York' },
        sessionId: { type: String, required: true },
        // Admin scheduler: caption shown above the slot list (the V2 wizard is
        // ET-only, hence the legacy default), and the 48h business-rule flag
        // forwarded to /dates/ + /times/.
        tzLabel: { type: String, default: 'EST · GMT-5' },
        enforceMinimum: { type: Boolean, default: false },
        // Payer / non-payer routing (flow/scheduling/advisor_tiers): the client's
        // tier, sent with /dates/ and /times/ so the backend drops the advisors
        // the tier excludes. null (unknown) sends nothing.
        payer: { type: Boolean, default: null },
    },
    data () {
        return {
            // The Sunday the visible 4-week window starts on.
            windowStart: this.minWindowStart(),
            datesByMonth: {}, // '2026-08' -> [Date objects]
            // Filled in by our own /dates/ response; takes precedence over the
            // prefetched `soonest` prop once we have a real answer.
            soonestDayLocal: null, // Date
            slots: [], // [{time, agent, agents}]
            loading: false,
        }
    },
    computed: {
        tierBody () {
            return typeof this.payer === 'boolean' ? { payer: this.payer } : {}
        },
        // months / dowShort now come from the store (single source of truth).
        ...mapGetters(['months', 'dowShort']),
        cells () {
            return Array.from({ length: WINDOW_DAYS }, (_, i) => ({ d: this.addDays(this.windowStart, i) }))
        },
        windowEnd () { return this.addDays(this.windowStart, WINDOW_DAYS - 1) },
        // The 'YYYY-MM' keys the window touches — one, or two when it
        // straddles a month (or year) boundary.
        windowMonthKeys () {
            const keys = [moment(this.windowStart).format('YYYY-MM'), moment(this.windowEnd).format('YYYY-MM')]
            return keys[0] === keys[1] ? [keys[0]] : keys
        },
        // Header label: "September 2026", "September – October 2026", or
        // "December 2026 – January 2027" across a year boundary.
        windowLabel () {
            const a = this.windowStart
            const b = this.windowEnd
            if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
                return `${this.months[a.getMonth()]} ${a.getFullYear()}`
            }
            if (a.getFullYear() === b.getFullYear()) {
                return `${this.months[a.getMonth()]} – ${this.months[b.getMonth()]} ${a.getFullYear()}`
            }
            return `${this.months[a.getMonth()]} ${a.getFullYear()} – ${this.months[b.getMonth()]} ${b.getFullYear()}`
        },
        canPrev () {
            return this.windowStart.getTime() > this.minWindowStart().getTime()
        },
        canNext () {
            // Under aep, Dec 7 is the last bookable day — a window past it
            // would only hold dates /book/ refuses (and, past December,
            // months /dates/ rejects outright).
            if (this.aep) {
                const nextStart = this.addDays(this.windowStart, WINDOW_DAYS)
                const lastCall = new Date(this.windowEnd.getFullYear(), AEP_LAST_MONTH, AEP_LAST_DAY)
                return nextStart.getTime() <= lastCall.getTime()
            }
            // No fixed forward cap otherwise: agents/clients can page as far
            // out as Acuity actually has availability; windows past that just
            // show no bookable days.
            return true
        },
        availableDateSet () {
            const all = this.windowMonthKeys.flatMap(k => this.datesByMonth[k] || [])
            return new Set(all.map(d => this.dateKey(d)))
        },
        // Best soonest we know about right now: our own /dates/ response if
        // it's landed, else the orchestrator's prefetched value for the
        // currently-selected method.
        soonestDay () {
            if (this.soonestDayLocal) return this.soonestDayLocal
            const s = this.soonest && this.soonest[this.method]
            return s ? moment(s, 'YYYY-MM-DD').toDate() : null
        },
        soonestLabel () {
            return this.soonestDay ? this.formatShortDate(this.soonestDay) : null
        },
        spansMonths () { return this.windowMonthKeys.length > 1 },
    },
    watch: {
        agents () {
            // The agent pool changed (e.g. a returning client narrowed it via
            // "work with my agent again?", #570) — the cached availability and
            // soonest belong to the old pool, so drop both and refetch.
            this.datesByMonth = {}
            this.soonestDayLocal = null
            this.loadDates()
        },
        // Any window change (manual paging OR a programmatic jump to the
        // soonest opening) loads whatever months the now-visible window
        // touches. Centralizing here is what makes "Soonest opening" load
        // the target window's dates.
        windowStart: 'loadDates',
        method () {
            // Switching method means a different schedule — drop everything
            // cached from the previous method so the prop's value (or the
            // next /dates/ response) takes over.
            this.datesByMonth = {}
            this.soonestDayLocal = null
            this.loadDates()
        },
        aep () {
            // The effective AEP flag can flip with the Step 1 screener answer
            // — every cached month belongs to the other appointment type, and
            // the bookable-window floor moves with it.
            this.datesByMonth = {}
            this.soonestDayLocal = null
            const start = this.minWindowStart()
            if (start.getTime() !== this.windowStart.getTime()) {
                this.windowStart = start // its watcher reloads
            } else {
                this.loadDates()
            }
        },
        'value.date': 'loadTimes',
    },
    mounted () {
        this.loadDates()
        if (this.value.date) {
            this.loadTimes()
        }
    },
    methods: {
        todayStart () {
            const t = new Date()
            t.setHours(0, 0, 0, 0)
            return t
        },
        weekStart (d) {
            const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
            x.setDate(x.getDate() - x.getDay())
            return x
        },
        addDays (d, n) {
            return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
        },
        // The earliest Sunday the window may start on: this week — floored to
        // the week of Oct 1 under aep, where /dates/ rejects earlier months
        // outright ("AEP months are October, November, and December") and
        // nothing before it is bookable. Live from Sep 1, when AEP scheduling
        // opens before the AEP months do (Aziz's 2026-09-01 400-spam finding).
        minWindowStart () {
            let floor = this.todayStart()
            if (this.aep) {
                const october = new Date(floor.getFullYear(), 9, 1)
                if (floor.getTime() < october.getTime()) floor = october
            }
            return this.weekStart(floor)
        },
        // Only months /dates/ accepts under aep. The clamped window can still
        // CONTAIN late-September cells (Oct 1's week starts on a Sunday in
        // September) — those simply render unavailable, but their month must
        // never be queried.
        isAepMonthKey (key) {
            const m = Number(key.slice(5))
            return m >= 10 && m <= 12
        },
        dateKey (d) { return moment(d).format('YYYY-MM-DD') },
        monthAbbr (d) { return this.months[d.getMonth()].slice(0, 3).toUpperCase() },
        sameDay (a, b) {
            return a && b &&
                a.getFullYear() === b.getFullYear() &&
                a.getMonth() === b.getMonth() &&
                a.getDate() === b.getDate()
        },
        isCellDisabled (cell) {
            if (cell.d.getTime() < this.todayStart().getTime()) return true
            return !this.availableDateSet.has(this.dateKey(cell.d))
        },
        dayClasses (cell) {
            // Compare the 'YYYY-MM-DD' strings directly. Using new Date(value.date)
            // would parse the date as UTC midnight, which renders as the previous
            // day in negative-offset timezones (e.g. EST) — highlighting the wrong cell.
            const selected = this.value.date && this.dateKey(cell.d) === this.value.date
            return {
                today: this.sameDay(cell.d, this.todayStart()),
                'has-slots': this.availableDateSet.has(this.dateKey(cell.d)),
                selected,
            }
        },
        changeWindow (delta) {
            const next = this.addDays(this.windowStart, delta * WINDOW_DAYS)
            // Paging back from a jumped-to window can overshoot the floor
            // (jumps land on arbitrary weeks, paging moves in 4-week strides).
            const min = this.minWindowStart()
            this.windowStart = next.getTime() < min.getTime() ? min : next
            // The windowStart watcher triggers loadDates() for the new window.
        },
        async loadDates () {
            if (!this.agents.length || this.method === 'email') return
            const wanted = this.windowMonthKeys.filter(k => !this.aep || this.isAepMonthKey(k))
            const missing = wanted.filter(k => !this.datesByMonth[k])
            if (!missing.length) {
                this.captureSoonest()
                return
            }
            this.loading = true
            try {
                await Promise.all(missing.map(async (month) => {
                    const resp = await this.$api.$post('/api/scheduling/dates/', {
                        agents: this.agents,
                        month,
                        timezone: this.timezone,
                        method: this.method,
                        aep: this.aep,
                        session_id: this.sessionId,
                        enforce_minimum: this.enforceMinimum,
                        ...this.tierBody,
                    })
                    const dates = (resp.dates || []).map(s => moment(s, 'YYYY-MM-DD').toDate())
                    this.$set(this.datesByMonth, month, dates)
                }))
                this.captureSoonest()
            } catch (err) {
                this.$emit('error', err?.message || 'Could not load available dates.')
            } finally {
                this.loading = false
            }
        },
        captureSoonest () {
            if (this.soonestDayLocal) return
            const all = this.windowMonthKeys.flatMap(k => this.datesByMonth[k] || [])
            if (all.length) {
                this.soonestDayLocal = new Date(Math.min(...all.map(d => d.getTime())))
            }
        },
        async loadTimes () {
            if (!this.value.date || !this.agents.length || this.method === 'email') {
                this.slots = []
                return
            }
            this.loading = true
            try {
                const resp = await this.$api.$post('/api/scheduling/times/', {
                    agents: this.agents,
                    date: this.value.date,
                    timezone: this.timezone,
                    method: this.method,
                    aep: this.aep,
                    session_id: this.sessionId,
                    enforce_minimum: this.enforceMinimum,
                    ...this.tierBody,
                })
                this.slots = resp.times || []
            } catch (err) {
                this.slots = []
                this.$emit('error', err?.message || 'Could not load available times.')
            } finally {
                this.loading = false
            }
        },
        pickDate (d) {
            const iso = this.dateKey(d)
            this.$emit('input', { ...this.value, date: iso, time: null, agent: null, agents: null })
        },
        pickSlot (s) {
            // Bubble up; orchestrator places the hold and updates state.
            this.$emit('pick-slot', { time: s.time, agent: s.agent, agents: s.agents })
        },
        jumpToSoonest () {
            if (!this.soonestDay) return
            // Lead the window with the soonest opening's week.
            this.windowStart = this.weekStart(this.soonestDay)
            this.pickDate(this.soonestDay)
        },
        formatLongDate (iso) { return moment(iso).format('dddd, MMMM D') },
        formatShortDate (d) { return moment(d).format('ddd, MMM D') },
        formatSlotTime (iso) { return moment.tz(iso, this.timezone).format('h:mm A') },
    },
}
